import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

function WhatWeDo() {
  const items = [
    {
      id: 'fellowship',
      title: 'Fellowship of Education Scholars',
      description: 'A collaborative network mentoring early‑career researchers to produce policy‑relevant evidence.',
      ctaLabel: 'Join the Fellowship',
      to: '/research',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14v7" />
        </svg>
      )
    },
    {
      id: 'research',
      title: 'Research & Publishing',
      description: 'We generate datasets, policy briefs, and peer‑reviewed contributions that inform decision‑makers.',
      ctaLabel: 'Explore Research',
      to: '/research',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6M7 8h10M5 6h14v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6z" />
        </svg>
      )
    },
    {
      id: 'scholarships',
      title: 'Scholarships',
      description: 'Targeted financial support that expands access to quality education for deserving students.',
      ctaLabel: 'Apply for Scholarship',
      to: '/scholarships',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'edtech',
      title: 'EdTech Tools',
      description: 'Practical, lightweight tools that help students study better and track learning progress.',
      ctaLabel: 'Try Cognition Lab',
      to: '/cognition-lab',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    }
  ];

  return (
    <section id="what-we-do" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">What We Do</h2>
          <p className="text-lg sm:text-xl text-gray-600">Four pillars advancing research and improving learning outcomes.</p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.12 } } }}
          className="grid gap-6 sm:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
        >
          {items.map((item, idx) => (
            <motion.div
              key={item.id}
              id={item.id}
              variants={{ hidden: { opacity: 0, x: idx % 2 === 0 ? -60 : 60, scale: 0.98 }, show: { opacity: 1, x: 0, scale: 1 } }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              whileHover={{ y: -6, scale: 1.02 }}
              whileTap={{ scale: 0.99 }}
              className="group bg-gradient-to-b from-gray-50 to-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-all duration-300 focus-within:ring-2 focus-within:ring-[rgb(130,88,18)]"
            >
              <div className="w-12 h-12 rounded-lg bg-[rgb(130,88,18)]/10 text-[rgb(130,88,18)] flex items-center justify-center mb-4">
                {item.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-600 mb-4">{item.description}</p>
              <Link
                to={item.to}
                className="inline-flex items-center gap-2 text-[rgb(130,88,18)] font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)] rounded"
                aria-label={`${item.ctaLabel} – ${item.title}`}
              >
                {item.ctaLabel}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default WhatWeDo;


